import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Button, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Divider,
} from '@mui/material';
import SaveRoundedIcon   from '@mui/icons-material/SaveRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon    from '@mui/icons-material/AddRounded';
import axios from 'axios';
import { AuthContext }  from '../../components/signin/AuthContext';
import { useGrowSetup } from '../../GrowSetupContext';
import { useConfig }    from '../../components/ConfigContext';
import V4ConfigLicht      from '../components/ConfigLicht';
import V4ConfigTemperatur from '../components/ConfigTemperatur';
import V4ConfigAbluft     from '../components/ConfigAbluft';

function SectionLabel({ children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Typography sx={{ fontSize:'0.62rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.16em', color:'rgba(var(--v4-cyan-rgb),0.6)', whiteSpace:'nowrap' }}>
        {children}
      </Typography>
      <Divider sx={{ flex:1, borderColor:'rgba(var(--v4-cyan-rgb),0.08)' }} />
    </Stack>
  );
}

const glassCard = {
  background: 'rgba(var(--v4-surface-rgb),0.9)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.1)',
  borderRadius: '18px',
  backdropFilter: 'blur(20px)',
  p: 3,
};


export default function V4ConfigPage() {
  const { token }      = useContext(AuthContext);
  const { grow_setup } = useGrowSetup();
  const { state }      = useConfig();
  const [presets,  setPresets]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [dlgOpen,  setDlgOpen]  = useState(false);
  const [newName,  setNewName]  = useState('');
  const [saving,   setSaving]   = useState(false);

  // SmallConfigLicht crashes if sun_rise is not yet a string — wait until config is loaded
  const configReady = typeof state.config.sun_rise === 'string';

  const loadPresets = () => {
    if (!grow_setup) return;
    setLoading(true);
    axios.post('/get_presets', { grow_setup_id: grow_setup }, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setPresets(Array.isArray(r.data) ? r.data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPresets(); }, [grow_setup]);

  const handleSavePreset = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await axios.post('/save_preset', { grow_setup_id: grow_setup, name: newName }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDlgOpen(false); setNewName(''); loadPresets();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/delete_preset/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadPresets();
    } catch (e) { console.error(e); }
  };

  return (
    <Box>
      {/* Preset manager */}
      <SectionLabel>Presets</SectionLabel>
      <Box sx={{ ...glassCard, mb: 4 }}>
        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
          {loading ? <CircularProgress size={20} sx={{ color: 'var(--v4-cyan)' }} /> : null}
          {presets.map(p => (
            <Chip
              key={p.id}
              label={p.name}
              deleteIcon={<DeleteRoundedIcon sx={{ fontSize: '0.85rem !important' }} />}
              onDelete={() => handleDelete(p.id)}
              sx={{
                background: 'rgba(var(--v4-cyan-rgb),0.08)',
                border: '1px solid rgba(var(--v4-cyan-rgb),0.2)',
                color: 'var(--v4-cyan)',
                fontWeight: 600,
                '& .MuiChip-deleteIcon': { color: 'rgba(var(--v4-red-soft-rgb),0.6)', '&:hover': { color: 'var(--v4-red)' } },
              }}
            />
          ))}
          <Button
            startIcon={<AddRoundedIcon />} size="small" variant="outlined"
            onClick={() => setDlgOpen(true)}
            sx={{ borderColor: 'rgba(var(--v4-cyan-rgb),0.3)', color: 'var(--v4-cyan)', borderRadius: '20px', '&:hover': { borderColor: 'var(--v4-cyan)', background: 'rgba(var(--v4-cyan-rgb),0.08)' } }}
          >
            Preset speichern
          </Button>
        </Stack>
      </Box>

      {/* Light config */}
      <SectionLabel>Licht</SectionLabel>
      <Box sx={{ mb: 4 }}>
        {configReady
          ? <V4ConfigLicht />
          : <Box sx={{ display:'flex', justifyContent:'center', py:6 }}><CircularProgress size={28} sx={{ color:'var(--v4-cyan)' }} /></Box>
        }
      </Box>

      {/* Temperature config */}
      <SectionLabel>Temperatur</SectionLabel>
      <Box sx={{ mb: 4 }}>
        {configReady
          ? <V4ConfigTemperatur />
          : <Box sx={{ display:'flex', justifyContent:'center', py:6 }}><CircularProgress size={28} sx={{ color:'var(--v4-cyan)' }} /></Box>
        }
      </Box>

      {/* Ventilation config */}
      <SectionLabel>Abluft</SectionLabel>
      <Box>
        {configReady
          ? <V4ConfigAbluft />
          : <Box sx={{ display:'flex', justifyContent:'center', py:6 }}><CircularProgress size={28} sx={{ color:'var(--v4-cyan)' }} /></Box>
        }
      </Box>

      {/* Save preset dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)}
        PaperProps={{ sx: { background: 'rgba(var(--v4-surface2-rgb),0.97)', border: '1px solid rgba(var(--v4-cyan-rgb),0.2)', borderRadius: '18px', backdropFilter: 'blur(24px)' } }}
      >
        <DialogTitle sx={{ color: 'var(--v4-text)', fontWeight: 700 }}>Preset benennen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Preset-Name"
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { background: 'rgba(var(--v4-cyan-rgb),0.05)', borderRadius: '10px', '& fieldset': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgOpen(false)} sx={{ color: 'rgba(var(--v4-muted-rgb),0.4)' }}>Abbrechen</Button>
          <Button
            onClick={handleSavePreset} disabled={saving} variant="contained"
            startIcon={<SaveRoundedIcon />}
            sx={{ background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))', boxShadow: '0 4px 16px rgba(var(--v4-cyan-rgb),0.35)', borderRadius: '10px' }}
          >
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
